import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedUserDto, TeamRemoteControllerService, UserRemoteControllerService } from 'core/api';
import { ITeamDto } from 'core/dto';
import { DynamicFormService, MultiAutocompleteLoadFn } from 'core/dynamic-form';
import { RemoteItemService, RemoteItemStore } from 'core/remotes';
import { LoaderService, MessageBoxService, ScreenUtilsService, SnackbarService } from 'core/services';
import { BehaviorSubject, filter, map, of, skip, switchMap, take, takeUntil, tap } from 'rxjs';
import { designerPaths } from '../../../designer.paths';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { LCrudMode } from '../../../shared-components/crud-toolbar/crud-toolbar.component';


@Injectable()
export class TeamManagementDetailService extends RemoteItemService<ITeamDto> {

  readonly loaderKeys = [
    'team.by-key',
    'team.update',
    'team.insert',
    'team.delete'
  ]

  readonly teamIdSubject = this.keyValueSubject;
  readonly mainFormGroupSubject = this._dynamicFormService.mainFormGroupSubject
  get #form() {
    return this.mainFormGroupSubject.value
  }
  get #teamId() {
    return this.teamIdSubject.value
  }

  readonly modeSubject = new BehaviorSubject<LCrudMode>('save');

  get #formData() {
    return this.#form.value as { name: string, userIds: string[] }
  }

  readonly #userMapper = (user: ExtendedUserDto) => ({
    ...user,
    display: user.firstName + ' ' + user.lastName,
    largeDisplay: user.firstName + ' ' + user.lastName + ' - ' + user.email,
    value: user.id
  });

  readonly loadFn: MultiAutocompleteLoadFn = (search: string, skip: number, take: number) => {
    search = search ? '%' + search + '%' : undefined
    return this._userRemoteControllerService.getUsersByCriteria({
      email: search,
      firstName: search,
      lastName: search,
      phone: undefined,
      isEligibleTeamMember: true,
      page: skip / take,
      size: take,
      sort: ['firstName']
    })
      .pipe(
        map(users => users.data.map(this.#userMapper))
      )
  }


  readonly initialSelectedItemsObs = this.remoteDataSubject.pipe(
    filter(Boolean),
    map((data) => data.users.map(this.#userMapper))
  )

  readonly displayExprObs = this._screenUtilsService.isSmallerScreenObs.pipe(
    map((isSmallerScreen) => isSmallerScreen ? 'display' : 'largeDisplay')
  )



  #inserted: ITeamDto

  constructor(
    private readonly _userRemoteControllerService: UserRemoteControllerService,
    private readonly _teamRemoteControllerService: TeamRemoteControllerService,
    private readonly _dynamicFormService: DynamicFormService,
    private readonly _router: Router,
    private readonly _messageBoxService: MessageBoxService<DsgDeepTranslationKey>,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _location: Location



  ) {
    super();
    super.setConfig({
      customStore: new RemoteItemStore({
        keyField: 'id',
        keyValue: null,
        load: (id: string) => this.#inserted ? of(this.#inserted) : this._teamRemoteControllerService.getOne(id),
        remove: (id) => this._teamRemoteControllerService.remove(id),
        update: (id, updateDto) => this._teamRemoteControllerService.update(id, updateDto),
        insert: (createDto) => this._teamRemoteControllerService.create(createDto).pipe(
          tap(dto => {
            this.#inserted = dto;
            this.teamIdSubject.next(dto.id);
            this._location.replaceState([designerPaths.teamManagement, dto.id].join('/'))


          }),
          map(dto => dto.id)
        ),

      }),

    })



    this.teamIdSubject.pipe(
      skip(1),
      takeUntil(this._destroy)
    )
      .subscribe((teamId) => {
        if (teamId === undefined) {
          this.modeSubject.next('save');
          return;
        }

        this.modeSubject.next('update');
      })

    this._dynamicFormService.controls = [
      {
        dataField: 'name',
        label: 'ss-entities.team.name',
        type: 'text',
        validationRules: [{ type: 'required' }]
      },
      {
        dataField: 'userIds',
        label: 'ss-entities.team.user-ids',
        type: 'multiautocomplete',
        validationRules: [{ type: 'required' }]
      },
    ]


    this.remoteDataSubject.pipe(
      filter(Boolean),
      map(data => ({ name: data.name, userIds: data.users.map(user => user.id) })),
      takeUntil(this._destroy)
    )
      .subscribe((formData) => {
        this.#form.patchValue(formData)
      })
  }

  reset() {




    this.localItemStore.stateSubject.pipe(
      filter(Boolean),
      take(1),
      map(data => ({ name: data.name, userIds: data.users.map(user => user.id) })),
      takeUntil(this._destroy)
    )
      .subscribe((formData) => {
        this.#form.patchValue(formData)
      })
  }

  save() {
    if (!this.#form.valid) {
      return;
    }

    if (this.#teamId) {
      const id = this._loaderService.show({ key: 'team.update', text: 'ss-entities.team.actions.update.pending' })
      this.update({ name: this.#formData.name, users: this.#formData.userIds.map(id => ({ id })) }, 'updateLocal').pipe(
        this._loaderService.disposeLoader(id),
        this._snackbarService.handleAsync({ errorMessage: 'ss-entities.team.actions.update.error', successMessage: 'ss-entities.team.actions.update.success' }),
        take(1)
      )
        .subscribe()
      return;
    }

    const id = this._loaderService.show({ key: 'team.insert', text: 'ss-entities.team.actions.insert.pending' })
    this.insert({ name: this.#formData.name, users: this.#formData.userIds.map(id => ({ id })) }).pipe(
      this._loaderService.disposeLoader(id),
      this._snackbarService.handleAsync({ errorMessage: 'ss-entities.team.actions.insert.error', successMessage: 'ss-entities.team.actions.insert.success' }),
      take(1)
    ).subscribe()
  }

  delete() {
    this._messageBoxService.show({
      buttons: 'YesNo',
      text: 'ss-entities.team.actions.delete.are-you-sure',
      type: 'prompt',
      title: 'ss-entities.team.actions.delete.title',
      replace: {
        '@team': this.localItemStore.state?.name
      }
    })
      .pipe(
        take(1),
        filter(answer => answer === 'Yes'),
        switchMap(() => {
          const id = this._loaderService.show({ key: 'team.delete', text: 'ss-entities.team.actions.delete.pending' })
          return this.update({ name: 'delete', users: [] }, 'updateLocal').pipe(
            switchMap(() => this.remove())
          ).pipe(
            this._loaderService.disposeLoader(id),
            this._snackbarService.handleAsync({ errorMessage: 'ss-entities.team.actions.delete.error', successMessage: 'ss-entities.team.actions.delete.success' }),
            take(1)
          )
        })
      )
      .subscribe(() => {
        this._router.navigate([designerPaths.teamManagement])

      })

  }

  cancel() {
    if (this.#teamId) {
      this.modeSubject.next('update');
      return;
    }

    this._router.navigate([designerPaths.teamManagement])


  }
}
